import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext.tsx';
import { useUpdateProfile } from '../../lib/profile-queries.ts';
import { Button, Spinner } from '../../components/ui.tsx';
import { useToast } from '../../components/Toast.tsx';
import { ApiError } from '../../lib/api.ts';
import { TimeZoneSelect, detectedTimeZone } from './TimeZoneSelect.tsx';

/**
 * The one question the system asks before it starts.
 *
 * Every timestamp in the product — when a file was uploaded, when an invoice
 * was issued, every row of the audit log — is stored in UTC and rendered in
 * the viewer's zone. Guessing that zone silently means a whole day's work can
 * be filed against the wrong date and nobody notices until reconciliation.
 *
 * The browser's guess pre-selects the dropdown, but it is only a guess: it
 * follows the laptop, not the business, and a machine set up abroad or a VPN
 * will report the wrong thing. Confirming takes one click; being wrong takes
 * an afternoon to find.
 */
export function TimeZonePrompt() {
  const { user, reload } = useAuth();
  const toast = useToast();
  const update = useUpdateProfile();

  const [zone, setZone] = useState(() => detectedTimeZone());
  const detected = detectedTimeZone();

  // Pre-select the stored zone if there is one — a user who reaches this
  // screen a second time should see their own answer, not the guess.
  useEffect(() => {
    if (user?.timeZone) setZone(user.timeZone);
  }, [user?.timeZone]);

  if (!user || user.hasCompletedSetup) return null;

  async function confirm() {
    try {
      await update.mutateAsync({ timeZone: zone, completeSetup: true });
      await reload();
      toast.success('Timezone saved', `Times are shown in ${zone.replace(/_/g, ' ')}.`);
    } catch (caught) {
      toast.error(
        'Could not save the timezone',
        caught instanceof ApiError ? caught.message : 'Please try again.',
      );
    }
  }

  const sample = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date());

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-[rgba(15,23,42,0.45)] p-4">
      <div className="w-full max-w-lg rounded-xl border border-border-strong bg-surface p-6 shadow-lg">
        <h1 className="text-h2 text-ink">Which timezone are you in?</h1>
        <p className="mt-2 text-body-sm text-body">
          Every date and time in the system is shown in this zone — when a file was uploaded, when
          an invoice was issued, every line of the audit log. It is asked once, and you can change
          it later from your profile.
        </p>

        <div className="mt-5">
          <label
            htmlFor="setup-timezone"
            className="mb-1.5 block text-body-sm font-medium text-ink"
          >
            Timezone
          </label>
          <TimeZoneSelect id="setup-timezone" value={zone} onChange={setZone} />
          {zone === detected ? (
            <p className="mt-1.5 text-micro text-muted">
              Detected from this computer. Change it if the business runs on a different clock.
            </p>
          ) : null}
        </div>

        <div className="mt-4 rounded-lg border border-border bg-sunken px-4 py-3">
          <p className="text-micro uppercase tracking-wide text-muted">Right now that reads</p>
          <p className="mt-1 text-body font-medium text-ink">{sample}</p>
        </div>

        <div className="mt-6 flex justify-end">
          <Button variant="primary" onClick={() => void confirm()} disabled={update.isPending}>
            {update.isPending ? <Spinner label="Saving" /> : 'Confirm and continue'}
          </Button>
        </div>
      </div>
    </div>
  );
}
